// covered-by: frontends/carrie/src/utils/__tests__/memoryCitationFields.test.ts

/**
 * The saved memories an answer or a review comment rests on, read off its graph
 * citations.
 *
 * A memory arrives as an EDGE of a graph citation whose `src` starts `memory:`
 * (`utils/graphNodes.ts`). warhol draws one on its own node, relation
 * `cited_inline` or `stated`, when the answer uses it, and hangs one on a claim
 * with relation `mentions` when the memory names a cited rule.
 *
 * why ONE module reads the new fields: `supportText`, `supportIndex`,
 * `memoryText`, `memoryType`, `memoryCreatedAt` and `memoryOrigin` ride on that
 * edge, and the edge type is a loose record. Four surfaces need them (the inline
 * chip, the rail card, the review comment, the claim provenance); each reading
 * the raw edge with its own cast is how one of them would drift.
 *
 * Every field is optional on the wire: an older chat's edge has the label only,
 * and the reader below shows as much as the edge carries.
 */

import { MEMORY_NODE } from "./graphNodes";
import { findSentence } from "./memoryTextMatch";

/**
 * How long warhol cuts the memory words it puts on an edge (`srcLabel`,
 * `text[:120]`). A label this long may have been cut mid-word.
 */
export const EDGE_LABEL_CHARS = 120;

/** The raw type a memory is saved as, which decides what its label says. */
export type MemoryType = string;

export interface MemoryCitation {
  /** The memory's graph node id without the `memory:` prefix. A key, not a link target. */
  id: string;
  /**
   * The memory; never blank. `memoryText` whole, else the edge's own label,
   * which warhol cuts to 120 characters (`clipped`).
   */
  text: string;
  /**
   * True when `text` is only the start of the memory: it came from the
   * 120-character edge label, cut at a word and closed with an ellipsis. The
   * whole memory is in Memories.
   */
  clipped?: boolean;
  /** The sentence of the reply the memory supports, markers stripped. */
  supportText?: string;
  /** Where that sentence sits among the reply's sentences; an ordering hint. */
  supportIndex?: number;
  type?: MemoryType;
  /** ISO date the memory was saved. */
  createdAt?: string;
  /** The pipeline that saved it, as its raw slug. */
  origin?: string;
}

/** A memory the answer or comment states a sentence for, with its number there. */
export interface NumberedMemory extends MemoryCitation {
  supportText: string;
  /** 1-based, per answer, in order of first appearance in the text. */
  number: number;
  /**
   * True when the sentence is found whole in the full text it was numbered
   * over; in a multi-segment answer the chip is drawn only by a segment whose
   * own text holds it (`memoryLinksBySegment`). False: the chip may still
   * follow the closest paragraph, or the memory shows in the rail only. Set by
   * `numberMemoryCitations`; absent on a memory a caller numbers itself.
   */
  inText?: boolean;
}

/**
 * A memory that is a preference of one user, applied to the answer but never
 * shown as evidence. warhol should not send one as a citation at all
 * (spec 0074 leaves classifying memories out); this is the belt to that brace.
 */
export const HIDDEN_MEMORY_TYPES: ReadonlySet<string> = new Set([
  "user_preference",
]);

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function whole(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : undefined;
}

/**
 * The start of a memory cut at a whole word, closed with an ellipsis. Warhol's
 * edge label is `text[:120]`, so its last word is often half of one.
 */
export function clipLabelAtWord(label: string): string {
  const cut = label.replace(/\s+\S*$/, "");
  const body = cut || label;
  return `${body.replace(/[\s,;:.\u2014-]+$/, "")}\u2026`;
}

/** True for an edge that names a saved memory that must not be drawn. */
export function isHiddenMemoryEdge(raw: unknown): boolean {
  const edge = raw as { memoryType?: unknown } | null;
  return HIDDEN_MEMORY_TYPES.has(text(edge?.memoryType));
}

/** True for an edge that carries the sentence the memory supports. */
export function hasSupportText(raw: unknown): boolean {
  const edge = raw as { supportText?: unknown } | null;
  return text(edge?.supportText) !== "";
}

/**
 * One graph edge as a memory citation, or null when it is not one: its source
 * is not a memory, it names no memory text to show, or it is a type that is
 * never shown.
 */
export function readMemoryEdge(raw: unknown): MemoryCitation | null {
  if (raw == null || typeof raw !== "object") return null;
  const edge = raw as Record<string, unknown>;
  const src = typeof edge.src === "string" ? edge.src : "";
  if (!src.startsWith(MEMORY_NODE)) return null;
  const id = src.slice(MEMORY_NODE.length);
  // why: `memoryText` is the whole memory and `srcLabel` is warhol's 120-character cut of it, so the label is read only for an edge that lacks the text.
  const full = text(edge.memoryText);
  const label = text(edge.srcLabel);
  const clipped = !full && label.length >= EDGE_LABEL_CHARS;
  const body = full || (clipped ? clipLabelAtWord(label) : label);
  if (!id || !body || isHiddenMemoryEdge(raw)) return null;
  const supportText = text(edge.supportText);
  const supportIndex = whole(edge.supportIndex);
  const type = text(edge.memoryType);
  const createdAt = text(edge.memoryCreatedAt);
  const origin = text(edge.memoryOrigin);
  return {
    id,
    text: body,
    ...(clipped ? { clipped } : {}),
    ...(supportText ? { supportText } : {}),
    ...(supportIndex !== undefined ? { supportIndex } : {}),
    ...(type ? { type } : {}),
    ...(createdAt ? { createdAt } : {}),
    ...(origin ? { origin } : {}),
  };
}

/**
 * One memory named by two edges, its own node and a claim it hangs on, as one record.
 *
 * The edge that names the sentence is the base, because only one of them knows
 * it; whatever the base lacks comes from the other edge, and a whole text beats
 * a label warhol cut at 120 characters. Without this the record that won took
 * its cut label to the popover while the other edge held the whole memory.
 */
export function mergeMemoryCitations(
  known: MemoryCitation,
  next: MemoryCitation,
): MemoryCitation {
  const swap = !known.supportText && !!next.supportText;
  const base = swap ? next : known;
  const other = swap ? known : next;
  const wholeFromOther = base.clipped === true && other.clipped !== true;
  const source = wholeFromOther ? other : base;
  const supportText = base.supportText ?? other.supportText;
  const supportIndex = base.supportIndex ?? other.supportIndex;
  const type = base.type ?? other.type;
  const createdAt = base.createdAt ?? other.createdAt;
  const origin = base.origin ?? other.origin;
  return {
    id: base.id,
    text: source.text,
    ...(source.clipped ? { clipped: true } : {}),
    ...(supportText ? { supportText } : {}),
    ...(supportIndex !== undefined ? { supportIndex } : {}),
    ...(type ? { type } : {}),
    ...(createdAt ? { createdAt } : {}),
    ...(origin ? { origin } : {}),
  };
}

/**
 * Every distinct memory the citations name, in the order the graph returned them.
 *
 * A memory named twice is one record (`mergeMemoryCitations`): the claim it
 * hangs on and its own node can both name it, and each knows something the
 * other does not.
 */
export function collectMemoryCitations(
  citations: readonly unknown[] | undefined,
): MemoryCitation[] {
  const byId = new Map<string, MemoryCitation>();
  for (const citation of citations ?? []) {
    const edges = (citation as { edges?: unknown } | null)?.edges;
    if (!Array.isArray(edges)) continue;
    for (const raw of edges) {
      const memory = readMemoryEdge(raw);
      if (!memory) continue;
      const known = byId.get(memory.id);
      byId.set(memory.id, known ? mergeMemoryCitations(known, memory) : memory);
    }
  }
  return Array.from(byId.values());
}

/** The memories that name a sentence they support; the rest keep the old rendering. */
export function supportedMemories(
  memories: readonly MemoryCitation[],
): (MemoryCitation & { supportText: string })[] {
  return memories.filter(
    (memory): memory is MemoryCitation & { supportText: string } =>
      !!memory.supportText,
  );
}

/**
 * Number the supported memories of one answer in order of first appearance.
 *
 * warhol's `supportIndex` is the sentence's place in the reply, so it orders
 * the memories first, whether or not the sentence is found in the text: the
 * numbers follow reading order even when a match fails. Where two share a
 * sentence index, or have none, the place the sentence is found decides; one
 * that is not found then comes after those that are, in the order warhol sent
 * them, since nothing else says where it reads.
 * Numbers are for memories only: claims keep their thread-wide numbers
 * (`threadCitationNumbers`) and are never mixed in.
 *
 * why numbered here and not in the rehype step: an answer is drawn as several
 * text segments, each its own Markdown render, and the rail needs the same
 * numbers without rendering any of them. One pure function over the reply and
 * the citations gives all three the same answer.
 */
export function numberMemoryCitations(
  content: string | undefined,
  memories: readonly MemoryCitation[],
): NumberedMemory[] {
  const reply = content ?? "";
  const placed = supportedMemories(memories).map((memory, order) => {
    const found = findSentence(reply, memory.supportText);
    return { memory, order, at: found?.start ?? -1 };
  });
  const rank = (value: number | undefined): number =>
    value ?? Number.MAX_SAFE_INTEGER;
  placed.sort(
    (a, b) =>
      rank(a.memory.supportIndex) - rank(b.memory.supportIndex) ||
      rank(a.at >= 0 ? a.at : undefined) - rank(b.at >= 0 ? b.at : undefined) ||
      a.order - b.order,
  );
  return placed.map(({ memory, at }, index) => ({
    ...memory,
    number: index + 1,
    inText: at >= 0,
  }));
}

/** A numbered memory and the answer or comment it is drawn in. */
export interface LinkableMemory extends NumberedMemory {
  /**
   * The message or comment id. Part of the key that links a chip, its sentence
   * and its rail card, and of every testid: the same memory can support two
   * answers of one thread.
   */
  scope: string;
  /**
   * False when the caller draws the chip itself, elsewhere (a review comment's
   * citation row): the sentence still takes its tint, but no chip follows it.
   */
  chip?: boolean;
}

/** The memories of one answer or comment, numbered and ready for the renderer. */
export function memoryLinksFor(
  citations: readonly unknown[] | undefined,
  content: string | undefined,
  scope: string,
): LinkableMemory[] {
  return numberMemoryCitations(content, collectMemoryCitations(citations)).map(
    (memory) => ({ ...memory, scope }),
  );
}

/**
 * Which text segment of an answer draws each memory: the first whose text
 * holds the memory's sentence. An answer of one segment draws every memory, so
 * the fallback can still place one whose sentence is not found whole.
 *
 * why: each segment is its own Markdown render, and handing every segment every
 * memory drew a memory whose sentence two segments hold twice, under one testid
 * (bobai, PR #6260). The match is `findSentence` over the segment's own text,
 * which is what `placedMemoryIds` reads, so a memory no single segment holds is
 * drawn in none and keeps its claim link.
 */
export function memoryLinksBySegment(
  segments: readonly (string | null)[],
  links: readonly LinkableMemory[],
): LinkableMemory[][] {
  const texts = segments.filter((text): text is string => text !== null);
  if (texts.length === 1) {
    return segments.map((text) => (text === null ? [] : [...links]));
  }
  const out: LinkableMemory[][] = segments.map(() => []);
  for (const link of links) {
    const at = segments.findIndex(
      (text) =>
        text !== null &&
        !!link.supportText &&
        findSentence(text, link.supportText) !== null,
    );
    if (at >= 0) out[at].push(link);
  }
  return out;
}
